import React from 'react';
import { SecondaryButton } from './buttons';

interface ExpiredLinkComponentProps {
  splitTitle?: string;
  collaboratorName?: string;
  ownerName?: string;
  expiresOn?: string;
}

const ExpiredLinkComponent: React.FC<ExpiredLinkComponentProps> = ({
  splitTitle,
  collaboratorName,
  ownerName,
  expiresOn
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'recently';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="min-h-screen bg-[#18181B] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-[#27272A] rounded-2xl shadow-xl p-8 text-center border border-[#3F3F46]">
        {/* Status Icon */}
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-[#F59E0B33] rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-[#F59E0B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Confirmation Link Expired
        </h1>

        {/* Expiry Information */}
        <div className="bg-[#F59E0B15] border-l-4 border-[#F59E0B] rounded-2xl p-4 mb-6 text-left">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-[#F59E0B]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              {splitTitle ? (
                <p className="text-[#FBBF24]">
                  The confirmation link for <strong>"{splitTitle}"</strong> has expired.
                </p>
              ) : (
                <p className="text-[#FBBF24]">
                  This confirmation link has expired and is no longer valid.
                </p>
              )}
              {collaboratorName && (
                <p className="text-[#F59E0B] mt-1">
                  Hi {collaboratorName}, this confirmation link expired on{' '}
                  <strong>{formatDate(expiresOn)}</strong> and is no longer valid.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">What can you do?</h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-[#3F3F46] rounded-2xl text-left border border-[#3F3F46]">
              <div className="flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-white">Contact the Split Creator</h4>
                <p className="text-[#9CA3AF] text-sm">
                  {ownerName ? (
                    <>Ask {ownerName} to resend the confirmation request from their dashboard</>
                  ) : (
                    <>Ask the split creator to resend the confirmation request from their dashboard</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-[#3F3F46] rounded-2xl text-left border border-[#3F3F46]">
              <div className="flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-white">Request New Link</h4>
                <p className="text-[#9CA3AF] text-sm">
                  Split creators can resend confirmation requests with fresh 7-day links
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="border-t border-[#3F3F46] pt-6 mb-6">
          <h4 className="font-medium text-white mb-2">Need Help?</h4>
          <p className="text-[#9CA3AF] text-sm">
            Contact support at{' '}
            <a 
              href="mailto:support@cotunemusic.com" 
              className="text-[#14B8A6] hover:text-[#0D9488] underline"
            >
              support@cotunemusic.com
            </a>
          </p>
        </div>

        {/* Action Button */}
        <SecondaryButton onClick={() => window.history.back()}>
          Go Back
        </SecondaryButton>
      </div>
    </div>
  );
};

export default ExpiredLinkComponent;
